import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  createAutosaveState,
  deriveFlushAction,
  deriveNextAction,
  isDirty,
  onEdit,
  onSaveConflict,
  onSaveFailed,
  onSaveLocked,
  onSaveRejected,
  onSaveStarted,
  onSaveSucceeded,
  QUOTE_DOCUMENT_CONFLICT_CODE,
  resolveConflictWithKeep,
  resolveConflictWithReload,
  type AutosaveConflict,
  type AutosaveState,
} from "@forgepoint/shared";
import { useQuoteDocumentsStore } from "./quoteDocumentsStore";

/** Idle delay before a debounced autosave fires, in milliseconds. */
const AUTOSAVE_DEBOUNCE_MS = 1500;

/** HTTP status the server returns for an optimistic-concurrency conflict. */
const CONFLICT_STATUS = 409;

/** Lowest and highest client-error statuses that permanently refuse a document. */
const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 499;

/** The autosave surface consumed by the editor tab. */
export interface AutosaveApi {
  /** True while a save request is in flight. */
  isSaving: boolean;
  /** True while there are unsaved edits pending a save. */
  isDirty: boolean;
  /** A persistent save error (e.g. a server validation message), or null. */
  saveError: string | null;
  /** A pending 409 conflict awaiting the user's choice, or null. */
  conflict: AutosaveConflict | null;
  /**
   * True once the quote was finalized on the server mid-edit: the document is
   * permanently read-only. The editor shows a locked notice rather than the
   * reload-or-keep dialog and stops attempting saves.
   */
  isLocked: boolean;
  /** Adopts the server document, resolving the conflict and going clean. */
  reloadServerVersion: () => void;
  /** Keeps the local document, re-saving it against the server version. */
  keepLocalVersion: () => void;
}

/**
 * Debounced, single-flight autosave for the quote-document editor, driven off
 * the editor's history commits (the changing document HTML). Each idle pause
 * persists the HTML with its optimistic-concurrency version through the store;
 * edits made mid-flight coalesce into the next save. A stale-version 409
 * surfaces a conflict for reload-or-keep recovery, a finalized-quote 409 locks
 * the editor read-only (no retry, no dialog), a 400-style rejection becomes a
 * persistent error while the local document is kept, and a `beforeunload` guard
 * warns while edits are pending. On unmount (e.g. an in-app switch back to the Quote
 * tab) any pending edit is flushed with a final immediate PUT so nothing is
 * silently dropped mid-debounce - unless the user explicitly discarded, in
 * which case the flush is suppressed and the server document is left alone.
 * @param quoteId - The saved quote's id backing the document PUT
 * @param documentHtml - The editor's current document HTML (the commit signal)
 * @param seedVersion - The version the editor's document was read at
 * @param onServerDocument - Adopts the server's HTML on a reload resolution
 * @param onSaved - Called after a successful save with the saved HTML/version
 * @param discardedRef - True once the user discarded, suppressing the unmount flush
 * @returns The autosave surface consumed by the editor tab
 */
export function useAutosave(
  quoteId: string,
  documentHtml: string,
  seedVersion: number,
  onServerDocument: (html: string) => void,
  onSaved: (html: string, version: number) => void,
  discardedRef: MutableRefObject<boolean>,
): AutosaveApi {
  const saveDocument = useQuoteDocumentsStore((store) => store.saveDocument);
  const fetchDocument = useQuoteDocumentsStore((store) => store.fetchDocument);

  const [state, setState] = useState<AutosaveState>(() =>
    createAutosaveState(documentHtml, seedVersion),
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const debounceRef = useRef<number | null>(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const onServerDocumentRef = useRef(onServerDocument);
  onServerDocumentRef.current = onServerDocument;
  const saveDocumentRef = useRef(saveDocument);
  saveDocumentRef.current = saveDocument;
  const quoteIdRef = useRef(quoteId);
  quoteIdRef.current = quoteId;
  const inFlightSaveRef = useRef<ReturnType<typeof saveDocument> | null>(null);

  const runSave = useCallback(
    async (document: string, expectedVersion: number) => {
      setState((current) => onSaveStarted(current, document));
      const pending = saveDocument(quoteId, {
        document,
        expectedVersion,
      });
      inFlightSaveRef.current = pending;
      const result = await pending;
      if (inFlightSaveRef.current === pending) {
        inFlightSaveRef.current = null;
      }
      if (result.document) {
        onSavedRef.current(result.document.document, result.document.version);
        setState((current) =>
          onSaveSucceeded(
            current,
            result.document?.document ?? document,
            result.document?.version ?? expectedVersion,
          ),
        );
        return;
      }
      if (result.status === CONFLICT_STATUS) {
        // A finalized quote is locked, not a version conflict: switch to a
        // terminal read-only state instead of the retry-prone reload/keep flow.
        if (result.conflictCode === QUOTE_DOCUMENT_CONFLICT_CODE.FINALIZED) {
          // Adopt the stored document before locking, so the read-only view
          // shows what the finalized quote actually uses rather than local
          // edits that were refused and will never be persisted.
          const stored = await fetchDocument(quoteId);
          if (stored) {
            onServerDocumentRef.current(stored.document);
            onSavedRef.current(stored.document, stored.version);
          }
          setState((current) =>
            onSaveLocked(
              current,
              stored
                ? {
                    serverDocument: stored.document,
                    serverVersion: stored.version,
                  }
                : undefined,
            ),
          );
          return;
        }
        const server = await fetchDocument(quoteId);
        if (!server) {
          setState((current) =>
            onSaveFailed(
              current,
              "Could not load the current document to resolve the conflict",
            ),
          );
          return;
        }
        setState((current) =>
          onSaveConflict(current, {
            serverDocument: server.document,
            serverVersion: server.version,
          }),
        );
        return;
      }
      const message = result.error ?? "Failed to save quote document";
      // A 4xx means the server will refuse this exact document however often we
      // resend it, so stop until the user edits. Anything else - a network
      // error (status 0) or a 5xx - is transient and must stay retryable, or
      // the pending edits are stranded until the user happens to type again.
      if (isPermanentRejection(result.status)) {
        setState((current) => onSaveRejected(current, message, document));
        return;
      }
      setState((current) => onSaveFailed(current, message));
    },
    [fetchDocument, quoteId, saveDocument],
  );

  useEffect(() => {
    setState((current) => onEdit(current, documentHtml));
  }, [documentHtml]);

  useEffect(() => {
    const action = deriveNextAction(state);
    if (action.kind !== "schedule-save") {
      return;
    }
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      runSave(action.document, action.expectedVersion);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [runSave, state]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty(stateRef.current) || stateRef.current.inFlightDocument) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(
    () => () => {
      if (discardedRef.current) {
        return;
      }
      const flush = deriveFlushAction(stateRef.current);
      if (flush.kind !== "flush-save") {
        return;
      }
      // Fire one last detached PUT so edits made within the debounce window are
      // not lost when an in-app tab switch unmounts the editor. There is no
      // React state left to update, so a 409/400 on this save is a noop.
      const pending = inFlightSaveRef.current;
      if (!pending) {
        saveDocumentRef.current(quoteIdRef.current, {
          document: flush.document,
          expectedVersion: flush.expectedVersion,
        });
        return;
      }
      // A save is still in flight, so flush.expectedVersion is already stale.
      // Chain the final PUT onto it and base it on the version that save
      // returns, otherwise the two requests race and this newer one loses.
      pending.then((result) => {
        if (!result.document) {
          return;
        }
        if (result.document.document === flush.document) {
          return;
        }
        saveDocumentRef.current(quoteIdRef.current, {
          document: flush.document,
          expectedVersion: result.document.version,
        });
      });
    },
    [discardedRef],
  );

  const reloadServerVersion = useCallback(() => {
    const conflict = stateRef.current.conflict;
    if (!conflict) {
      return;
    }
    onServerDocumentRef.current(conflict.serverDocument);
    // Push the adopted version up to the tab's seed too, or closing the
    // now-clean editor shows a stale preview and reopening reseeds the old
    // HTML/version straight into another conflict.
    onSavedRef.current(conflict.serverDocument, conflict.serverVersion);
    setState((current) => resolveConflictWithReload(current));
  }, []);

  const keepLocalVersion = useCallback(() => {
    setState((current) => resolveConflictWithKeep(current));
  }, []);

  return {
    isSaving: state.inFlightDocument !== null,
    isDirty: isDirty(state),
    saveError: state.error,
    conflict: state.conflict,
    isLocked: state.locked,
    reloadServerVersion,
    keepLocalVersion,
  };
}

/**
 * Whether a failed save status means the server permanently refuses this
 * document, as opposed to a transient failure worth retrying.
 * @param status - The HTTP status of the failed save, or 0 for a network error
 * @returns True when the document should not be resent unchanged
 */
function isPermanentRejection(status: number): boolean {
  return status >= CLIENT_ERROR_MIN && status <= CLIENT_ERROR_MAX;
}
